import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { isChunkLoadError } from "@/utils/chunkLoadRecovery";

type LazyModule<T> = { default: T };

async function retryDynamicImport<T>(
  factory: () => Promise<LazyModule<T>>,
  retriesLeft: number,
  delayMs: number
): Promise<LazyModule<T>> {
  try {
    return await factory();
  } catch (error) {
    if (retriesLeft <= 0 || !isChunkLoadError(error)) {
      throw error;
    }
    await new Promise<void>(resolve => {
      window.setTimeout(resolve, delayMs);
    });
    return retryDynamicImport(factory, retriesLeft - 1, delayMs);
  }
}

/** React.lazy mit Retry bei Chunk-Load-Fehlern (Deploy / instabiles Netz). */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<LazyModule<T>>,
  options?: { retries?: number; delayMs?: number }
): LazyExoticComponent<T> {
  const retries = options?.retries ?? 2;
  const delayMs = options?.delayMs ?? 800;
  return lazy(() => retryDynamicImport(factory, retries, delayMs));
}
